//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  Fragment, KeyboardEvent, MouseEvent, ReactNode, SyntheticEvent,
  useCallback, useEffect, useRef, useState
} from 'react';
import { useNavigate } from 'react-router';
import {
  Box, Chip, ClickAwayListener, Divider, Grow,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Paper, Popper, Tab, Tabs, Tooltip, Typography
} from '@mui/material';
import {
  ChevronLeft,
  DeviceHub as DeviceHubIcon,
  Image as ImageIcon,
  LibraryBooks as DocumentationIcon,
  OpenInNew as ExternalLinkIcon,
  QuestionMark as QuestionIcon,
  School as TutorialIcon,
} from '@mui/icons-material';
import { styled, useTheme as useMuiTheme } from '@mui/material/styles';
import HeaderControl from '@/components/Header/HeaderControl';
import { useHelp } from '@/hooks/useHelp';
import { useUI } from '@/hooks/useUI';
import { useAuth } from '@/hooks/useAuth';
import {
  ArchitectureDiagramWithFileType, getArchitectureCategories,
  getArchitectureDiagrams, getDiagramById
} from './architecture/architectureConfig';
import ArchitectureDiagram from './architecture/ArchitectureDiagram';
import {
  DocumentationLink, getDocumentationCategories, getDocumentationLinks
} from './documentation/documentationLinks';
import {
  getAvailableTours, getTourCategories, getTourInfo
} from './tours/tourSteps';

const DisabledChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: '22px',
  marginTop: theme.spacing(0.5),
  fontWeight: 500,
  position: 'relative', // Needed for ::before.
  backgroundColor: '#ffffff !important',
  border: `2px solid ${theme.palette.warning.main} !important`,
  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  opacity: '1 !important',
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    color: `${theme.palette.text.primary} !important`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 'inherit',
    boxShadow: `0 0 0 1px ${theme.palette.warning.light}`,
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));

const HelpIcon = () => {
  const theme = useMuiTheme();
  const navigate = useNavigate();

  const {
    helpState: { isPulsing },
    tourActions: { startTour },
    helpActions: { hideHelpIndicators, cancelInactivityTimer },
    tourFocus
  } = useHelp();
  const { isAuthenticated } = useAuth();
  const { displayMode } = useUI();

  // Local component state.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  // 0 for Tours, 1 for Documentation, 2 for Architecture.
  const [activeTab, setActiveTab] = useState(0);
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const helpButtonRef = useRef<HTMLButtonElement | null>(null);
  const helpMenuRef = useRef<HTMLElement | null>(null);
  const subMenuRef = useRef<HTMLElement | null>(null);

  // The floor of a tour's exit focus chain. Published on every render
  // rather than once on mount, so the ref cannot be left pointing at a
  // button from an earlier mount of this component.
  useEffect(() => { tourFocus.fallbackRef.current = helpButtonRef.current; });

  // Timeout for hover delay.
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedDiagram, setSelectedDiagram] =
    useState<ArchitectureDiagramWithFileType | null>(null);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [architectureFilter, setArchitectureFilter] = useState('all');

  // Get categories and tours.
  const availableTours = getAvailableTours(isAuthenticated());
  const categories = getTourCategories(isAuthenticated());
  const regularTours = availableTours.filter(tour => !tour.category);
  const categoryTours =
    hoveredCategory ?
    availableTours.filter(tour => tour.category === hoveredCategory) : [];

  // Get documentation links with product name.
  const mainDocLinks = getDocumentationLinks();
  const docCategories = getDocumentationCategories();

  // Get architecture data.
  const architectureDiagrams =
    getArchitectureDiagrams(architectureFilter === 'all' ?
                            null : architectureFilter);
  const architectureCategories = getArchitectureCategories();

  // Handle tab change.
  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSubMenuOpen(false);
    setHoveredCategory(null);
  };

  // Handle help button click.
  const handleToggle = useCallback((event: MouseEvent<HTMLElement>) => {
    // Hide indicators and cancel any active timer.
    hideHelpIndicators();
    cancelInactivityTimer();

    // Set local component state.
    setAnchorEl(event.currentTarget);
    setOpen((prevOpen) => {
      // Hide tooltip when opening menu, restore when closing (user is still
      // hovering).
      setHelpTooltipOpen(prevOpen);
      return !prevOpen;
    });
    setSubMenuOpen(false);
    setHoveredCategory(null);
  }, [hideHelpIndicators, cancelInactivityTimer]);

  // Handle click away.
  const handleClose = useCallback(() => {
    setOpen(false);
    setHelpTooltipOpen(false);
    setSubMenuOpen(false);
    setHoveredCategory(null);

    // Clear any pending hover timers.
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  // Handle starting a tour.
  const handleStartTour = useCallback((tourId: string) => {
//    console.log("handleStartTour(): ", tourId);
    handleClose();

    // Validate tour exists.
    const tourInfo = getTourInfo(tourId, isAuthenticated());
    if (!tourInfo) {
      console.error(`[HelpContext] Tour not found: ${tourId}`);
      return;
    }

    startTour(tourId);
  }, [handleClose, startTour, isAuthenticated]);

  // Handle external link click.
  const handleExternalLink = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleClose(); // Close menu after opening external link.
  }, [handleClose]);

  // Handle internal link click.
  const handleInternalLink = useCallback((route: string) => {
    navigate(route);
//    handleClose(); // Close menu after opening external link.
  }, [navigate]);

  // Open a category's tours beside its row. The row is a menu item, so this
  // is what Enter, Space and a tap do, as well as the hover below: a keyboard
  // or touch user cannot hover.
  const openCategory = useCallback((categoryId: string) => {
    if (activeTab !== 0) return; // Only for the Tours tab.
    setSubMenuAnchorEl(categoryRefs.current[categoryId]);
    setHoveredCategory(categoryId);
    setSubMenuOpen(true);
  }, [activeTab]);

  // Handle category hover enter.
  const handleCategoryMouseEnter = useCallback((categoryId: string) => {
    // Clear any existing hover timeout.
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Set a small timeout to prevent flickering on quick mouse movements.
    hoverTimerRef.current = setTimeout(() => openCategory(categoryId), 100);
  }, [openCategory]);

  // Handle category hover leave.
  const handleCategoryMouseLeave = useCallback(() => {
    // We don't immediately close the submenu to allow moving to it.
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
  }, []);

  // Handle submenu mouse enter.
  const handleSubMenuMouseEnter = useCallback(() => {
    // Clear any existing hover timeout that might close the submenu.
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  // Handle submenu mouse leave.
  const handleSubMenuMouseLeave = useCallback(() => {
    // Set timeout to close submenu.
    if (hoverTimerRef.current)
      clearTimeout(hoverTimerRef.current);

    hoverTimerRef.current = setTimeout(() => {
      setSubMenuOpen(false);
      setHoveredCategory(null);
    }, 200);
  }, []);

  // New handlers for architecture diagrams.
  const handleOpenDiagram = useCallback((diagramId: string) => {
    const diagram = getDiagramById(diagramId);
    if (diagram) {
      setSelectedDiagram(diagram);
      setDiagramOpen(true);

      // Close the help menu.
      handleClose();
    }
  }, [handleClose]);

  const handleCloseDiagram = useCallback(() => {
    setDiagramOpen(false);
  }, []);

  const handleArchitectureFilter = useCallback((categoryId: string) => {
    setArchitectureFilter(categoryId);
  }, []);

  // Clean up timers when component unmounts.
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, []);

  // Focus active tab when help menu opens.
  const focusHelpMenu = () => {
    if (!helpMenuRef.current) return;
    const tabs = helpMenuRef.current.querySelectorAll<HTMLElement>('[role="tab"]');
    if (tabs.length > 0 && tabs[activeTab]) {
      tabs[activeTab].focus();
    } else {
      const firstItem = helpMenuRef.current.querySelector<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])');
      if (firstItem) {
        firstItem.focus();
      } else {
        helpMenuRef.current.focus();
      }
    }
  };

  // Focus first enabled menu item in submenu.
  const focusSubMenu = () => {
    if (!subMenuRef.current) return;
    const firstItem = subMenuRef.current.querySelector<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])');
    if (firstItem) {
      firstItem.focus();
    } else {
      subMenuRef.current.focus();
    }
  };

  // Keyboard handler for help menu navigation (includes tabs).
  const handleHelpMenuKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const menu = event.currentTarget;
    const tabs = Array.from(menu.querySelectorAll<HTMLElement>('[role="tab"]'));
    const filterChips = Array.from(
      menu.querySelectorAll<HTMLElement>('[data-filter-chip="true"]'));
    const listItems = Array.from(menu.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([data-filter-chip]):not([aria-disabled="true"])'));
    const isOnTab = tabs.some(t => t === document.activeElement);
    const isOnChip = filterChips.some(c => c === document.activeElement);
    const chipIndex = filterChips.findIndex(c => c === document.activeElement);
    const listIndex = listItems.findIndex(item => item === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (isOnTab) {
          // Move from tab to first filter chip or list item.
          if (filterChips.length > 0) {
            filterChips[0].focus();
          } else if (listItems.length > 0) {
            listItems[0].focus();
          }
        } else if (isOnChip) {
          // Move from chip to first list item.
          if (listItems.length > 0) {
            listItems[0].focus();
          }
        } else {
          // Navigate within list items.
          if (listItems.length === 0) return;
          if (listIndex < listItems.length - 1) {
            listItems[listIndex + 1].focus();
          } else {
            // Wrap to tab.
            if (tabs.length > 0) {
              tabs[activeTab].focus();
            }
          }
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOnTab) {
          // Wrap to last list item.
          if (listItems.length > 0) {
            listItems[listItems.length - 1].focus();
          } else if (filterChips.length > 0) {
            filterChips[filterChips.length - 1].focus();
          }
        } else if (isOnChip) {
          // Move from chip to tab.
          if (tabs.length > 0) {
            tabs[activeTab].focus();
          }
        } else {
          // Navigate within list items.
          if (listItems.length === 0) return;
          if (listIndex > 0) {
            listItems[listIndex - 1].focus();
          } else {
            // Move to last filter chip or tab.
            if (filterChips.length > 0) {
              filterChips[filterChips.length - 1].focus();
            } else if (tabs.length > 0) {
              tabs[activeTab].focus();
            }
          }
        }
        break;
      case 'ArrowLeft':
        if (isOnChip) {
          event.preventDefault();
          if (chipIndex > 0) {
            filterChips[chipIndex - 1].focus();
          } else {
            filterChips[filterChips.length - 1].focus();
          }
        }
        // Otherwise let MUI's native Tabs keyboard handling work.
        break;
      case 'ArrowRight':
        if (isOnChip) {
          event.preventDefault();
          if (chipIndex < filterChips.length - 1) {
            filterChips[chipIndex + 1].focus();
          } else {
            filterChips[0].focus();
          }
        }
        // Otherwise let MUI's native Tabs keyboard handling work.
        break;
      case 'Escape':
      case 'Tab':
        event.preventDefault();
        handleClose();
        if (helpButtonRef?.current) {
          helpButtonRef.current.focus();
        }
        break;
      default:
        break;
    }
  }, [activeTab, handleClose]);

  // Keyboard handler for submenu navigation.
  const handleSubMenuKeyDown =
      useCallback((event: KeyboardEvent<HTMLElement>) => {
    const menu = event.currentTarget;
    const items = Array.from(menu.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])'));
    const currentIndex =
      items.findIndex(item => item === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (items.length === 0) return;
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1].focus();
        } else {
          items[0].focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (items.length === 0) return;
        if (currentIndex > 0) {
          items[currentIndex - 1].focus();
        } else {
          items[items.length - 1].focus();
        }
        break;
      case 'Escape':
      case 'Tab':
        event.preventDefault();
        setSubMenuOpen(false);
        setHoveredCategory(null);
        if (helpButtonRef?.current) {
          helpButtonRef.current.focus();
        }
        break;
      default:
        break;
    }
  }, []);

  const docsListItem = (link: DocumentationLink): ReactNode => {
    return link.type === 'external' ? (
      <ListItem component="div" key={link.id} disablePadding>
        <ListItemButton role="menuitem" onClick={() => handleExternalLink(link.url)}>
          {link.icon && <ListItemIcon>{link.icon}</ListItemIcon>}
          <ListItemText
            primary={link.title}
            secondary={link.description}
            slotProps={{
              primary: { sx: { fontWeight: 'medium' } }
            }}
          />
          <ExternalLinkIcon fontSize="small" color="action" />
        </ListItemButton>
      </ListItem>
    ) : link.type === 'internal' ? (
      <ListItem component="div" key={link.id} disablePadding>
        <ListItemButton role="menuitem" onClick={() => handleInternalLink(link.url)}>
          {link.icon && <ListItemIcon>{link.icon}</ListItemIcon>}
          <ListItemText
            primary={link.title}
            secondary={link.description}
            slotProps={{
              primary: { sx: { fontWeight: 'medium' } }
            }}
          />
        </ListItemButton>
      </ListItem>
    ) : null;
  };

  return (
    <>
      {/* Floating button container with visibility control for tours. */}
      <Tooltip
        title="Click for guided tours and other help"
        open={!open && helpTooltipOpen}
        onOpen={() => setHelpTooltipOpen(true)}
        onClose={() => setHelpTooltipOpen(false)}
      >
        <span>
          <HeaderControl
            ref={helpButtonRef}
            icon={<QuestionIcon fontSize="inherit" />}
            label="Help"
            // The fuller phrasing stays the accessible name in every display
            // mode, matching the tooltip above rather than shrinking to the
            // bare label once titles or icons are hidden.
            accessibleName="Help and Tours"
            isLabelHidden={displayMode === 'icons'}
            isIconHidden={displayMode === 'titles'}
            hasExternalTooltip
            isPulsing={isPulsing}
            tier={open ? 'filled' : 'ghost'}
            popup="dialog"
            isExpanded={open}
            onClick={handleToggle}
          />
        </span>
      </Tooltip>
      {/* Help menu popper */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        placement="bottom-end"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            // Add a bit of space between the help icon and the popup window.
            name: 'offset',
            options: { offset: [0, 5] },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }} onEntered={focusHelpMenu}>
            <Paper
              elevation={6}
              sx={{
                width: { xs: 320, sm: 380 },
                maxHeight: '80vh',
                overflow: 'auto'
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box
                  ref={helpMenuRef}
                  role="dialog"
                  aria-label="Help and Tours"
                  tabIndex={-1}
                  onKeyDown={handleHelpMenuKeyDown}
                >
                  {/* Header with Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="fullWidth"
                      sx={{ minHeight: '64px' }}
                    >
                      <Tab
                        label="Tours"
                        icon={<TutorialIcon />}
                        iconPosition="top"
                        sx={{
                          minHeight: '64px',
                          py: 1,
                          fontSize: '0.875rem',
                        }}
                      />
                      <Tab
                        label="Docs"
                        icon={<DocumentationIcon />}
                        iconPosition="top"
                        sx={{
                          minHeight: '64px',
                          py: 1,
                          fontSize: '0.875rem',
                        }}
                      />
                      <Tab
                        label="Architecture"
                        icon={<DeviceHubIcon />}
                        iconPosition="top"
                        sx={{
                           minHeight: '64px',
                          py: 1,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      />
                    </Tabs>
                  </Box>

                  {/* Tours Tab */}
                  {activeTab === 0 && (
                    <List component="div" sx={{ pt: 0 }} role="menu">
                      {regularTours.map((tour) => {
                        const isDisabled = Boolean(tour.disabledReason);

                        return (
                          <ListItem component="div" key={tour.id} disablePadding>
                            <ListItemButton
                              role="menuitem"
                              onClick={() => !isDisabled && handleStartTour(tour.id)}
                              disabled={isDisabled}
                              sx={{
                                opacity: isDisabled ? 0.6 : 1,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {tour.icon && <ListItemIcon>{tour.icon}</ListItemIcon>}
                              <ListItemText
                                primary={tour.title}
                                secondary={
                                  <>
                                    {tour.description}
                                    {isDisabled && (
                                      <DisabledChip label={tour.disabledReason} />
                                    )}
                                  </>
                                }
                                slotProps={{
                                  primary: { sx: { fontWeight: 'medium' } }
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}

                      {categories.length > 0 && (
                        <Divider sx={{ my: 1 }}>
                          <Typography variant="caption" sx={{
                            color: 'text.secondary'
                          }}>
                            CATEGORIES
                          </Typography>
                        </Divider>
                      )}

                      {categories.map((category) => {
                        const isDisabled = Boolean(category.disabledReason);

                        return (
                          <ListItem
                            component="div"
                            key={category.id}
                            disablePadding
                            ref={(el) => { categoryRefs.current[category.id] = el; }}
                            onMouseEnter={() => !isDisabled && handleCategoryMouseEnter(category.id)}
                            onMouseLeave={() => !isDisabled && handleCategoryMouseLeave()}
                          >
                            <ListItemButton
                                role="menuitem"
                                disabled={isDisabled}
                                onClick={() =>
                                  !isDisabled && openCategory(category.id)}
                                sx={{
                                  opacity: isDisabled ? 0.6 : 1,
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                }}
                            >
                              {category.icon && <ListItemIcon>{category.icon}</ListItemIcon>}
                              <Box>
                                <ListItemText
                                  primary={category.title}
                                  secondary={`${category.description} (${category.tourCount})`}
                                  slotProps={{
                                    primary: { sx: { fontWeight: 'medium' } }
                                  }}
                                />
                                {isDisabled && (
                                  <DisabledChip label={category.disabledReason} />
                                )}
                              </Box>
                              <ChevronLeft fontSize="small" />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}

                  {/* Documentation Tab */}
                  {activeTab === 1 && (
                    <List component="div" sx={{ pt: 0 }} role="menu">
                      {mainDocLinks.map((link) => docsListItem(link))}

                      {docCategories.map((category) => (
                        <Fragment key={category}>
                          <Divider sx={{ my: '4px' }}>
                            <Typography variant="caption" sx={{
                              color: 'text.secondary'
                            }}>
                              {category}
                            </Typography>
                          </Divider>

                          {getDocumentationLinks(category).map((link) => docsListItem(link))}
                        </Fragment>
                      ))}
                    </List>
                  )}

                  {/* Architecture Tab */}
                  {activeTab === 2 && (<>
                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      {/* Filter chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} role="menu" aria-label="Filter diagrams">
                        {architectureCategories.map(category => (
                          <Chip
                            key={category.id}
                            role="menuitem"
                            data-filter-chip="true"
                            label={category.name}
                            size="small"
                            onClick={() => handleArchitectureFilter(category.id)}
                            color={architectureFilter === category.id ? 'primary' : 'default'}
                            variant={architectureFilter === category.id ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Box>
                    </Box>

                    <List component="div" sx={{ pt: 0 }} role="menu">
                      {architectureDiagrams.map((diagram) => (
                        <ListItem component="div" key={diagram.id} disablePadding>
                          <ListItemButton role="menuitem" onClick={() => handleOpenDiagram(diagram.id)}>
                            <ListItemIcon><ImageIcon /></ListItemIcon>
                            <ListItemText
                              primary={diagram.title}
                              secondary={diagram.description}
                              slotProps={{
                                primary: { sx: { fontWeight: 'medium' } }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </>)}

                  <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption" sx={{
                      color: 'text.secondary'
                    }}>
                      {activeTab === 0 ?
                        'Click a tour to launch.  Tours can be canceled at any time.' :
                        activeTab === 1 ?
                        'External links will open in a new browser window.' :
                        'Click a diagram to view it.'
                      }
                    </Typography>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {/* A category's tours, beside the row that opens them.  Leftward
          because the help menu itself hangs from the header's right edge,
          which leaves no room on the right; the category row's chevron
          points the same way.  Popper's flip modifier stays on, so a
          viewport with no room on the left either can still swing it back. */}
      <Popper
        open={subMenuOpen}
        anchorEl={subMenuAnchorEl}
        transition
        placement="left-start"
        style={{ zIndex: 1301 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }} onEntered={focusSubMenu}>
            <Paper
              elevation={6}
              sx={{
                width: { xs: 320, sm: 380 }, // Should match main menu width.
                maxHeight: '80vh',
                overflow: 'auto'
              }}
              onMouseEnter={handleSubMenuMouseEnter}
              onMouseLeave={handleSubMenuMouseLeave}
            >
              <Box
                ref={subMenuRef}
                role="dialog"
                aria-label="Sub-menu"
                tabIndex={-1}
                onKeyDown={handleSubMenuKeyDown}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="h6">
                    {categories.find(cat => cat.id === hoveredCategory)?.title || 'Tours'}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary'
                  }}>
                    Select a specific tour
                  </Typography>
                </Box>
                <List component="div" sx={{ pt: 0 }} role="menu">
                  {categoryTours.map((tour) => {
                    const isDisabled = Boolean(tour.disabledReason);

                    return (
                      <ListItem component="div" key={tour.id} disablePadding>
                        <ListItemButton
                          role="menuitem"
                          onClick={() => !isDisabled && handleStartTour(tour.id)}
                          disabled={isDisabled}
                          sx={{
                            opacity: isDisabled ? 0.6 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {tour.icon && <ListItemIcon>{tour.icon}</ListItemIcon>}
                          <ListItemText
                            primary={tour.title}
                            secondary={
                              <>
                                {tour.description}
                                {isDisabled && (
                                  <DisabledChip label={tour.disabledReason} />
                                )}
                              </>
                            }
                            slotProps={{
                              primary: { sx: { fontWeight: 'medium' } }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Paper>
          </Grow>
        )}
      </Popper>
      {/* Architecture Diagram Modal */}
      <ArchitectureDiagram
        open={diagramOpen}
        onClose={handleCloseDiagram}
        diagram={selectedDiagram}
      />
    </>
  );
};

// Add a display name for better debugging.
HelpIcon.displayName = 'HelpIcon';

export default HelpIcon;
